import Alert from '@seller-hermes/features/ui-v2/Alert';
import Command from '@seller-hermes/features/ui-v2/Command';
import Skeleton from '@seller-hermes/features/ui-v2/Skeleton';
import Switch from '@seller-hermes/features/ui-v2/Switch';
import SellerChip from '@seller-hermes/features/ui-v2/seller-chip';
import { SellerMarkersQuery } from '@seller-hermes/features/tags/seller-markers.query';
import { SellerMarker } from '@seller-hermes/features/tags/seller-markers.dto';
import { cn } from '@seller-hermes/utils/cn';
import { Check } from 'lucide-react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
// Imports diretos dos paths internos pra evitar o bug do index.js do
// react-virtualized que tenta importar `bpfrpt_proptype_WindowScroller`
// (export que não existe) e quebra o build do Vite/Rollup.
// https://github.com/bvaughn/react-virtualized/issues/1722
import AutoSizer from 'react-virtualized/dist/es/AutoSizer';
import VirtualList from 'react-virtualized/dist/es/List';
import 'react-virtualized/styles.css';
import { Button } from '../../ui-v2/Button';
import Dialog from '../../ui-v2/Dialog';
import { Seller } from './sellers-selector.types';
import {
  CountryFlags,
  SellerCountries,
} from '@seller-hermes/features/sellers/new-seller-dialog/new-seller-dialog.schema';
import { useQuery, useQueryClient } from 'react-query';
import { FetchKeys } from '@seller-hermes/features/ui/constants';
import { useSellersLookup } from './use-sellers-lookup';

// Nome pt-BR de cada país (mesma lista de SellerCountries). Usado como label
// do filtro `pais:` no autocomplete; o valor inserido é o código (bra/arg/…).
const COUNTRY_LABELS: Record<SellerCountries, string> = {
  [SellerCountries.ARG]: 'Argentina',
  [SellerCountries.BRA]: 'Brasil',
  [SellerCountries.CHL]: 'Chile',
  [SellerCountries.COL]: 'Colômbia',
  [SellerCountries.MEX]: 'México',
  [SellerCountries.PER]: 'Peru',
};

// Emoji da bandeira a partir do ISO-3166 alpha-2 (br → 🇧🇷): cada letra vira o
// regional indicator symbol correspondente.
const flagEmoji = (iso2: string) =>
  iso2
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

// Referência estável pro default do useQuery — sem isso, `= []` cria um array
// novo a cada render quando `data` é undefined, deixando facetValues/
// updateAutocomplete instáveis e disparando o effect em loop.
const EMPTY_MARKERS: SellerMarker[] = [];

// Remove acentos/diacríticos pra busca ser acento-insensível:
// "guaruja" casa "Guarujá", "sao paulo" casa "São Paulo".
const stripAccents = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '');

// Facets de filtro. Usado no autocomplete rápido de CHAVE: assim que você
// começa a digitar "esta"/"cid"/"marc", sugere a tag do filtro em chip pra
// completar `estado:`/`cidade:`/`marcador:` num toque.
const FACET_KEYS = [
  { value: 'estado', label: 'Estado' },
  { value: 'cidade', label: 'Cidade' },
  { value: 'pais', label: 'País' },
  { value: 'marcador', label: 'Marcador' },
] as const;

interface SelectSellersPopupProps extends React.ComponentProps<typeof Dialog> {
  sellers: Seller[];
  onSelect: (sellers: Seller[]) => void;
  defaultValue: Seller[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  sellersIDontHaveAccess?: Seller[];
  minimumAllowedSelectedSellers?: number;
  useAllSellers?: boolean;
  onUseAllSellersChange?: (enabled: boolean) => void;
  // SUP-425/426: alguma página do carregamento em segundo plano falhou
  // mesmo depois do retry — a lista pode estar incompleta. Não bloqueia o
  // popup, só avisa (ver useSellersLookup).
  hasLoadError?: boolean;
}

const SelectSellersDialog: React.FC<SelectSellersPopupProps> = ({
  open,
  onOpenChange,
  sellers: sellersFromProps,
  onSelect,
  defaultValue,
  title,
  description,
  sellersIDontHaveAccess = [],
  minimumAllowedSelectedSellers = 1,
  useAllSellers = false,
  onUseAllSellersChange,
  hasLoadError = false,
}) => {
  const MIN_SELECTED_SELLERS = minimumAllowedSelectedSellers;

  // O toggle "Ver todas as filiais" (que inclui novas filiais automaticamente)
  // só faz sentido no seletor global da sidebar, que controla o estado
  // `useAllSellers` do grupo. Nos demais usos (relatórios, usuários, motoristas…)
  // o seletor salva uma lista fixa de filiais — mostrar o toggle ali engana o
  // usuário sobre a auto-inclusão. A presença do handler é o sinal de que o
  // consumidor realmente suporta esse comportamento (só a sidebar o passa).
  const showAllSellersToggle = Boolean(onUseAllSellersChange);

  // Projeção enxuta do seletor (GET /v2/sellers/lookup), paginada em segundo
  // plano (SUP-425) via hook compartilhado com o pai (mesma chave de cache —
  // as duas telas enxergam a mesma lista à medida que ela carrega).
  // Fallback pros sellers de props enquanto a 1ª página ainda não voltou.
  const {
    sellers: enrichedSellersData,
    total: lookupTotal,
    isLoading: isLoadingSellers,
    isLoadingMore: isLoadingMoreSellers,
  } = useSellersLookup();
  const sellers = React.useMemo<Seller[]>(() => {
    const base =
      enrichedSellersData && enrichedSellersData.length
        ? enrichedSellersData
        : sellersFromProps;
    // Só filiais ATIVAS no seletor: inativas não devem aparecer na lista nem
    // ser selecionáveis (inclusive via "selecionar tudo"). `!== false` mantém
    // entradas sem o campo definido, removendo apenas as explicitamente inativas.
    return base.filter((s) => s.active !== false);
  }, [enrichedSellersData, sellersFromProps]);

  // Skeleton só na primeira carga: enquanto a 1ª página do lookup roda e
  // ainda não temos dado enriquecido nem fallback de props pra mostrar.
  // Não espera as páginas seguintes (isLoadingMoreSellers) — o popup abre
  // assim que a 1ª chega, e o resto entra em segundo plano.
  const isInitialLoading =
    isLoadingSellers &&
    enrichedSellersData.length === 0 &&
    sellersFromProps.length === 0;

  const { t } = useTranslation('sellers-selector');
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const [value, setValue] = React.useState<string[]>(
    defaultValue.map((seller) => seller.id),
  );

  const [unmatchedTokens, setUnmatchedTokens] = React.useState<string[]>([]);
  const [searchValue, setSearchValue] = React.useState('');

  // Autocomplete inline: ativa quando o token sob o cursor casa com
  // `estado:` / `cidade:` / `marcador:`. Sugere valores reais do grupo.
  const [ac, setAc] = React.useState<{
    open: boolean;
    // 'facet' = sugerindo a CHAVE do filtro (estado:/cidade:/marcador:) em chips;
    // 'value' = sugerindo os VALORES reais daquele filtro.
    kind: 'facet' | 'value';
    key: 'estado' | 'cidade' | 'pais' | 'marcador' | null;
    items: Array<{
      value: string;
      label: string;
      sub?: string;
      count: number;
      color?: string;
      flag?: string;
    }>;
    idx: number;
    range: { start: number; end: number } | null;
    query: string;
    loading: boolean;
  }>({
    open: false,
    kind: 'value',
    key: null,
    items: [],
    idx: 0,
    range: null,
    query: '',
    loading: false,
  });

  // Sync internal state with defaultValue when it changes
  React.useEffect(() => {
    setValue(defaultValue.map((seller) => seller.id));
  }, [defaultValue]);

  const options = React.useMemo(() => {
    return sellers
      .sort((a, b) => a.trading_name.localeCompare(b.trading_name))
      .map((seller) => {
        // Pega o primeiro warehouse com endereço — é o que reflete a filial.
        // Quando backend devolver address direto no seller, dá pra simplificar.
        const address = seller.warehouses?.find((w) => w.address_object)
          ?.address_object as
          | {
              street?: string;
              street_number?: string;
              neighborhood?: string;
              city?: string;
              state?: string;
            }
          | undefined;
        const tags = (seller.seller_seller_tags || [])
          .map((a) => a.seller_tag)
          .filter(Boolean);
        const addressLine = [
          address?.street &&
            (address.street_number
              ? `${address.street}, ${address.street_number}`
              : address.street),
          address?.neighborhood,
        ]
          .filter(Boolean)
          .join(' · ');
        const facets = {
          estado: address?.state?.toLowerCase() || '',
          cidade: address?.city?.toLowerCase() || '',
          // País vem do seller (não do endereço): código lowercased ('bra') pro
          // filtro estruturado `pais:`, e nome ('brasil') pra busca livre.
          pais: seller.country?.toLowerCase() || '',
          paisNome: seller.country
            ? (COUNTRY_LABELS[seller.country] || '').toLowerCase()
            : '',
          marcadores: tags.map((t) => t.label.toLowerCase()),
          identifier: seller.identifier?.toLowerCase() || '',
          cnpjDigits: seller.document_number?.replace(/\D/g, '') || '',
          trading: seller.trading_name?.toLowerCase() || '',
          sellerId: seller.id,
          street: address?.street?.toLowerCase() || '',
          neighborhood: address?.neighborhood?.toLowerCase() || '',
        };
        return {
          facets,
          label: (
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex min-w-0 items-center gap-2">
                  <SellerChip
                    baseStringToColor={seller.id}
                    className="max-w-fit shrink-0"
                  >
                    {seller.trading_name}
                  </SellerChip>
                  {seller.identifier && (
                    <span className="text-muted-foreground shrink-0 font-mono text-[10px]">
                      {seller.identifier}
                    </span>
                  )}
                </div>
                <div className="text-muted-foreground flex min-w-0 items-center gap-2 text-[11px]">
                  <span className="font-mono shrink-0">
                    {seller.document_number}
                  </span>
                  {address?.city && address?.state && (
                    <>
                      <span className="shrink-0">·</span>
                      <span className="shrink-0">
                        {address.city}/{address.state}
                      </span>
                    </>
                  )}
                </div>
                {addressLine && (
                  <div className="text-muted-foreground/80 min-w-0 truncate text-[10.5px]">
                    {addressLine}
                  </div>
                )}
              </div>
              {tags.length > 0 && (
                <div className="flex shrink-0 flex-wrap justify-end gap-1">
                  {tags.map((t) => (
                    <span
                      key={t.id}
                      className="rounded-full border px-2 py-0.5 text-[10px] font-medium leading-tight"
                      style={{
                        borderColor: t.color,
                        color: t.color,
                        backgroundColor: `${t.color}1A`,
                      }}
                    >
                      {t.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ),
          name: seller.id,
          filterValue: [
            seller.trading_name,
            seller.id,
            seller.document_number,
            seller.identifier,
            address?.city,
            address?.state,
            ...tags.map((t) => t.label),
          ]
            .filter(Boolean)
            .join(','),
        };
      });
  }, [sellers]);

  // Lista de TODOS os marcadores criados (mesmo os sem filial atribuída).
  // Sem isso, o autocomplete de `marcador:` só mostraria os já em uso.
  const { data: allMarkers = EMPTY_MARKERS, isLoading: isLoadingMarkers } = useQuery({
    queryKey: [FetchKeys.SELLER_GROUP_SELLER_TAGS_LIST],
    queryFn: () => SellerMarkersQuery.getMarkersList(),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  // Valores possíveis pros autocompletes/dropdowns de estado/cidade/marcador.
  // Estado/cidade derivam dos sellers carregados; marcador parte de allMarkers
  // (assim aparecem todos os criados em /settings/tags), e o count é
  // sobreposto com os que estão de fato atribuídos pra sellers do grupo.
  const facetValues = React.useMemo(() => {
    const estados = new Map<string, number>();
    const cidades = new Map<string, { count: number; estado: string }>();
    // Países chaveados pelo código do enum ('BRA') pra resolver label/bandeira.
    const paises = new Map<SellerCountries, number>();
    const marcadores = new Map<string, { count: number; color: string }>();
    // Semeia com todos os marcadores criados (count 0 por padrão)
    for (const m of allMarkers) {
      marcadores.set(m.label, { count: 0, color: m.color });
    }
    for (const s of sellers) {
      if (s.country) {
        paises.set(s.country, (paises.get(s.country) || 0) + 1);
      }
      const addr = s.warehouses?.find((w) => w.address_object)?.address_object;
      if (addr?.state) {
        estados.set(addr.state, (estados.get(addr.state) || 0) + 1);
      }
      if (addr?.city) {
        const prev = cidades.get(addr.city);
        cidades.set(addr.city, {
          count: (prev?.count || 0) + 1,
          estado: addr.state || prev?.estado || '',
        });
      }
      for (const a of s.seller_seller_tags || []) {
        if (!a.seller_tag) continue;
        const prev = marcadores.get(a.seller_tag.label);
        marcadores.set(a.seller_tag.label, {
          count: (prev?.count || 0) + 1,
          color: a.seller_tag.color,
        });
      }
    }
    return { estados, cidades, paises, marcadores };
  }, [sellers, allMarkers]);

  // Lookups for bulk paste: identifier / CNPJ (digits only) / id.
  const lookups = React.useMemo(() => {
    const byIdentifier = new Map<string, string>();
    const byCnpj = new Map<string, string>();
    const byId = new Map<string, string>();
    for (const s of sellers) {
      if (s.identifier) byIdentifier.set(s.identifier.toUpperCase(), s.id);
      if (s.document_number) {
        const digits = s.document_number.replace(/\D/g, '');
        if (digits) byCnpj.set(digits, s.id);
      }
      byId.set(s.id, s.id);
    }
    return { byIdentifier, byCnpj, byId };
  }, [sellers]);

  // Paste of N tokens (vírgula / espaço / ; / quebra de linha / tab) auto-marca
  // os matches por identifier ou CNPJ e acumula os não encontrados num Alert.
  // Paste de 1 token cai no fluxo normal de search do cmdk.
  const handlePaste = React.useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (useAllSellers) return;
      const text = e.clipboardData.getData('text');
      if (!text) return;
      const tokens = text
        .replace(/[,;\n\t]+/g, ' ')
        .split(/\s+/)
        .map((t) => t.trim())
        // Ignora tokens em sintaxe de filtro (estado:SP, cidade:"X", marcador:Y)
        // — esses são pra busca, não pra lookup de seller.
        .filter((t) => t && !/^(estado|cidade|pais|marcador):/i.test(t));
      if (tokens.length < 2) return;
      e.preventDefault();
      const matched = new Set<string>();
      const unmatched: string[] = [];
      for (const tok of tokens) {
        const upper = tok.toUpperCase();
        const digits = tok.replace(/\D/g, '');
        const hit =
          lookups.byIdentifier.get(upper) ??
          (digits.length >= 10 ? lookups.byCnpj.get(digits) : undefined) ??
          lookups.byId.get(tok);
        if (hit) matched.add(hit);
        else unmatched.push(tok);
      }
      setValue((prev) => Array.from(new Set([...prev, ...matched])));
      setUnmatchedTokens(unmatched);
    },
    [lookups, useAllSellers],
  );

  const selectAll = React.useCallback(() => {
    setValue(options.map((o) => o.name));
  }, [options]);

  // Index do row destacado pela navegação por teclado (↑↓/Enter).
  // Vira `scrollToIndex` na lista virtualizada pra manter visível.
  const [activeIndex, setActiveIndex] = React.useState(0);

  // Ref da lista virtualizada pra rolar imperativamente. A prop `scrollToIndex`
  // do react-virtualized é instável (o Grid ignora updates depois de algumas
  // interações), então forçamos `scrollToRow` sempre que o activeIndex muda.
  const listRef = React.useRef<{ scrollToRow: (index: number) => void } | null>(
    null,
  );

  // Ref do item ativo no dropdown do autocomplete, pra rolar ele pra view na
  // navegação por ↑↓ (o container tem overflow próprio).
  const acActiveItemRef = React.useRef<HTMLButtonElement>(null);

  // Sintaxe `estado:SP cidade:"São Paulo" marcador:premium` + texto livre.
  // Normaliza vírgula/;/quebra fora de aspas como espaço (mesma regra do paste).
  // Match: facets fazem AND. Texto livre faz substring contra trading/identifier/
  // cnpj/cidade/estado/labels de marcador.
  const visibleOptions = React.useMemo(() => {
    const raw = searchValue.trim();
    if (!raw) return options;

    // 1. Normaliza separadores fora de aspas
    let normalized = '';
    let inQuote = false;
    for (let i = 0; i < raw.length; i++) {
      const ch = raw[i];
      if (ch === '"') {
        inQuote = !inQuote;
        normalized += ch;
        continue;
      }
      if (!inQuote && (ch === ',' || ch === ';' || ch === '\n' || ch === '\t')) {
        normalized += ' ';
        continue;
      }
      normalized += ch;
    }

    // 2. Tokeniza
    const tokens =
      normalized.match(/(\w+):"([^"]*)"|(\w+):(\S+)|"([^"]+)"|(\S+)/g) || [];
    const filters: {
      estado?: string;
      cidade?: string;
      pais?: string;
      marcador?: string;
    } = {};
    const textTokens: string[] = [];
    const FACETS = ['estado', 'cidade', 'pais', 'marcador'] as const;

    tokens.forEach((tok) => {
      const m = tok.match(/^(\w+):(.+)$/);
      if (m && (FACETS as readonly string[]).includes(m[1].toLowerCase())) {
        const key = m[1].toLowerCase() as
          | 'estado'
          | 'cidade'
          | 'pais'
          | 'marcador';
        filters[key] = m[2].replace(/^"|"$/g, '').toLowerCase();
      } else {
        const clean = tok.replace(/^"|"$/g, '').replace(/[,;]+$/, '');
        if (clean) textTokens.push(clean.toLowerCase());
      }
    });

    return options.filter((o) => {
      const f = o.facets;
      if (filters.estado && f.estado !== filters.estado) return false;
      if (
        filters.cidade &&
        !stripAccents(f.cidade).includes(stripAccents(filters.cidade))
      )
        return false;
      // País é match exato pelo código (autocomplete-driven, igual estado).
      if (filters.pais && f.pais !== filters.pais) return false;
      if (filters.marcador && !f.marcadores.includes(filters.marcador))
        return false;
      if (textTokens.length) {
        const haystack = stripAccents(
          [
            f.trading,
            f.identifier,
            f.cnpjDigits,
            f.cidade,
            f.estado,
            f.pais,
            f.paisNome,
            f.street,
            f.neighborhood,
            ...f.marcadores,
            f.sellerId.toLowerCase(),
          ].join(' '),
        );
        // OR entre os tokens de texto livre: o caso dominante deste seletor é
        // colar uma LISTA de filiais (identificadores/CNPJs separados por
        // vírgula/espaço). Dois identificadores diferentes nunca caem na mesma
        // filial, então AND zeraria a lista. Filtros estruturados continuam
        // sendo AND via estado:/cidade:/marcador:.
        const matchesAny = textTokens.some((t) => {
          // Casa direto (trading, identifier, cidade, etc) — acento-insensível
          if (haystack.includes(stripAccents(t))) return true;
          // Se o token tem dígitos (CNPJ/identifier formatado), tenta a versão
          // só-dígitos contra cnpjDigits. Cobre "00.776.574/0001-01".
          const digits = t.replace(/\D/g, '');
          if (digits.length >= 4 && f.cnpjDigits.includes(digits)) return true;
          return false;
        });
        if (!matchesAny) return false;
      }
      return true;
    });
  }, [options, searchValue]);



  // Snapshot de quais filiais estão marcadas SÓ pra fins de ordenação
  // (selecionadas em cima). Atualiza quando a busca muda ou o popup (re)abre —
  // NÃO a cada toggle. Sem isso, marcar uma filial fazia ela pular pro topo na
  // hora, embaralhando a lista embaixo do cursor enquanto você seleciona.
  const [orderSnapshot, setOrderSnapshot] = React.useState<Set<string>>(
    () => new Set(value),
  );
  React.useEffect(() => {
    setOrderSnapshot(new Set(value));
    // Intencional: re-ordena só quando a busca muda ou o popup abre, não a cada
    // mudança de `value` (senão volta a pular).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, open]);

  const [selectedOptions, unselectedOptions] = React.useMemo(() => {
    return [
      visibleOptions.filter((option) => orderSnapshot.has(option.name)),
      visibleOptions.filter((option) => !orderSnapshot.has(option.name)),
    ];
  }, [orderSnapshot, visibleOptions]);

  // Lista única pra virtualização: selecionadas em cima, depois as outras.
  // O destaque (bg amarelo) continua refletindo `value` em tempo real — só a
  // POSIÇÃO é congelada pelo snapshot, então o toggle não reordena a lista.
  const combinedList = React.useMemo(
    () => [...selectedOptions, ...unselectedOptions],
    [selectedOptions, unselectedOptions],
  );

  // Reseta o foco do teclado pra o topo sempre que a busca muda
  React.useEffect(() => {
    setActiveIndex(0);
  }, [searchValue]);

  // Mantém o activeIndex dentro do range válido quando o tamanho da lista muda
  React.useEffect(() => {
    if (activeIndex >= combinedList.length) {
      setActiveIndex(Math.max(0, combinedList.length - 1));
    }
  }, [combinedList.length, activeIndex]);

  // Rola a lista virtualizada pra manter o item ativo visível na navegação por
  // teclado (a prop `scrollToIndex` sozinha não é confiável no react-virtualized).
  React.useEffect(() => {
    listRef.current?.scrollToRow(activeIndex);
  }, [activeIndex]);

  // Rola o item ativo do autocomplete pra dentro do container quando navega
  // com ↑↓ (block: 'nearest' só move o scroll do dropdown, não a página).
  React.useEffect(() => {
    if (ac.open) {
      acActiveItemRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [ac.idx, ac.open]);

  const toggleAtIndex = React.useCallback(
    (idx: number) => {
      const item = combinedList[idx];
      if (!item) return;
      if (value.includes(item.name)) {
        setValue(value.filter((v) => v !== item.name));
      } else {
        setValue([...value, item.name]);
      }
    },
    [combinedList, value],
  );

  // Toggle: marca todos visíveis, ou desmarca se já estavam todos marcados
  const toggleVisible = React.useCallback(() => {
    const visibleIds = visibleOptions.map((o) => o.name);
    if (!visibleIds.length) return;
    const allSelected = visibleIds.every((id) => value.includes(id));
    if (allSelected) {
      setValue(value.filter((v) => !visibleIds.includes(v)));
    } else {
      setValue(Array.from(new Set([...value, ...visibleIds])));
    }
  }, [visibleOptions, value]);

  // Todas as filiais VISÍVEIS (após o filtro) já estão marcadas? Define o rótulo
  // do botão do rodapé entre "selecionar visíveis" e "desmarcar visíveis".
  const allVisibleSelected = React.useMemo(() => {
    const visibleIds = visibleOptions.map((o) => o.name);
    return visibleIds.length > 0 && visibleIds.every((id) => value.includes(id));
  }, [visibleOptions, value]);

  // Limpa TODA a seleção, inclusive filiais que não estão visíveis no filtro
  // atual (≠ toggleVisible, que só age sobre as visíveis).
  const clearSelection = React.useCallback(() => {
    setValue([]);
  }, []);

  // Computa o token sob o cursor + atualiza state de autocomplete
  const updateAutocomplete = React.useCallback(() => {
    const input = searchInputRef.current;
    if (!input) return;
    const value = input.value;
    const caret = input.selectionStart ?? value.length;

    // Token = sequência sem whitespace ao redor do cursor.
    // Não considera aspas dentro do token; cidade:"São Paulo" tem espaço,
    // então o token só é "cidade:\"São" — bom o suficiente pra suggerir
    // enquanto digita, porque a query é o que vier depois do `:`.
    let start = caret;
    while (start > 0 && !/\s/.test(value[start - 1])) start--;
    let end = caret;
    while (end < value.length && !/\s/.test(value[end])) end++;
    const text = value.slice(start, end);
    if (!text) {
      setAc((s) => ({ ...s, open: false }));
      return;
    }

    const m = text.match(/^(estado|cidade|pais|marcador):"?(.*)$/i);
    if (!m) {
      // Sem `:` ainda — autocomplete rápido da CHAVE do filtro: se o que está
      // sendo digitado é prefixo (≥2 letras) de estado/cidade/marcador, sugere
      // a tag em chip pra completar `chave:` num toque.
      const tokenLc = stripAccents(text.toLowerCase());
      const keyMatches =
        !text.includes(':') && tokenLc.length >= 2
          ? FACET_KEYS.filter((fk) => fk.value.startsWith(tokenLc))
          : [];
      if (keyMatches.length) {
        const facetItems = keyMatches.map((fk) => ({
          value: fk.value,
          label: fk.label,
          count:
            fk.value === 'estado'
              ? facetValues.estados.size
              : fk.value === 'cidade'
                ? facetValues.cidades.size
                : fk.value === 'pais'
                  ? facetValues.paises.size
                  : facetValues.marcadores.size,
        }));
        setAc((prev) => {
          const sameToken =
            prev.open &&
            prev.kind === 'facet' &&
            prev.range?.start === start &&
            prev.range?.end === end;
          const idx = sameToken
            ? Math.min(prev.idx, Math.max(0, facetItems.length - 1))
            : 0;
          return {
            open: true,
            kind: 'facet',
            key: null,
            items: facetItems,
            idx,
            range: { start, end },
            query: tokenLc,
            loading: false,
          };
        });
        return;
      }
      setAc((s) => ({ ...s, open: false }));
      return;
    }
    const key = m[1].toLowerCase() as
      | 'estado'
      | 'cidade'
      | 'pais'
      | 'marcador';
    const query = stripAccents(
      m[2].replace(/^"/, '').replace(/"$/, '').toLowerCase(),
    );

    let items: Array<{
      value: string;
      label: string;
      sub?: string;
      count: number;
      color?: string;
      flag?: string;
    }> = [];
    if (key === 'estado') {
      items = Array.from(facetValues.estados.entries())
        .filter(([uf]) => !query || stripAccents(uf.toLowerCase()).includes(query))
        .sort(([, a], [, b]) => b - a)
        .slice(0, 8)
        .map(([uf, count]) => ({ value: uf, label: uf, count }));
    } else if (key === 'cidade') {
      items = Array.from(facetValues.cidades.entries())
        .filter(([c]) => !query || stripAccents(c.toLowerCase()).includes(query))
        .sort(([, a], [, b]) => b.count - a.count)
        .slice(0, 8)
        .map(([c, info]) => ({
          value: c,
          label: c,
          sub: info.estado || undefined,
          count: info.count,
        }));
    } else if (key === 'pais') {
      items = Array.from(facetValues.paises.entries())
        // Casa pelo código ('bra') ou pelo nome ('brasil') — o que o usuário
        // digitou. O valor inserido no filtro é sempre o código.
        .filter(([code]) => {
          if (!query) return true;
          const name = stripAccents((COUNTRY_LABELS[code] || '').toLowerCase());
          return code.toLowerCase().includes(query) || name.includes(query);
        })
        .sort(([, a], [, b]) => b - a)
        .map(([code, count]) => ({
          value: code.toLowerCase(),
          label: COUNTRY_LABELS[code] || code,
          count,
          flag: CountryFlags[code] ? flagEmoji(CountryFlags[code]) : undefined,
        }));
    } else {
      items = Array.from(facetValues.marcadores.entries())
        .filter(([l]) => !query || stripAccents(l.toLowerCase()).includes(query))
        .sort(([, a], [, b]) => b.count - a.count)
        .slice(0, 8)
        .map(([l, info]) => ({
          value: l,
          label: l,
          count: info.count,
          color: info.color,
        }));
    }

    // Loading: marcador depende de SellerMarkersQuery; estado/cidade dependem
    // da SellersQuery.lookup. Mantém o dropdown aberto enquanto carrega pra
    // o usuário não pensar que tá quebrado.
    const loading =
      key === 'marcador' ? isLoadingMarkers : isLoadingSellers;

    // Sempre abre quando o token é um prefixo de filtro válido (estado:/
    // cidade:/marcador:). Assim o usuário SEMPRE tem feedback: spinner
    // enquanto carrega, lista quando tem, ou "Nenhum resultado" quando vazio.
    //
    // Preserva o idx quando o token (key+query+posição) não mudou — sem isso,
    // qualquer re-render que faça o effect rodar de novo (ex.: identidade
    // instável de updateAutocomplete) zeraria o idx logo depois da seta ↑/↓
    // mover a seleção, fazendo a navegação por teclado parecer quebrada.
    setAc((prev) => {
      const sameToken =
        prev.open &&
        prev.kind === 'value' &&
        prev.key === key &&
        prev.query === query &&
        prev.range?.start === start &&
        prev.range?.end === end;
      const idx = sameToken
        ? Math.min(prev.idx, Math.max(0, items.length - 1))
        : 0;
      return {
        open: true,
        kind: 'value',
        key,
        items,
        idx,
        range: { start, end },
        query,
        loading,
      };
    });
  }, [facetValues, isLoadingMarkers, isLoadingSellers]);

  // Substitui o token atual pela escolha (`key:value`) e move o cursor pra
  // depois, deixando um espaço pra o usuário continuar digitando.
  const applyAutocomplete = React.useCallback(() => {
    const input = searchInputRef.current;
    if (!input || !ac.open || !ac.range || !ac.items.length) return;
    const item = ac.items[ac.idx];
    const before = searchValue.slice(0, ac.range.start);
    const after = searchValue.slice(ac.range.end);

    // Modo chip de CHAVE: completa só `chave:` e deixa o cursor logo após o `:`
    // (sem espaço) — o effect de searchValue reabre o autocomplete já no modo
    // de valores daquele filtro.
    if (ac.kind === 'facet') {
      const replacement = `${item.value}:`;
      const newValue = before + replacement + after;
      const newCaret = (before + replacement).length;
      setSearchValue(newValue);
      setAc((s) => ({ ...s, open: false }));
      requestAnimationFrame(() => {
        if (input) {
          input.focus();
          input.setSelectionRange(newCaret, newCaret);
        }
      });
      return;
    }

    const needsQuote = /\s/.test(item.value);
    const valueStr = needsQuote ? `"${item.value}"` : item.value;
    const replacement = `${ac.key}:${valueStr}`;
    const newValue = before + replacement + ' ' + after.replace(/^\s+/, '');
    const newCaret = (before + replacement + ' ').length;
    setSearchValue(newValue);
    setAc((s) => ({ ...s, open: false }));
    // setSelectionRange precisa rodar depois do React aplicar o novo value
    requestAnimationFrame(() => {
      if (input) {
        input.focus();
        input.setSelectionRange(newCaret, newCaret);
      }
    });
  }, [ac, searchValue]);

  // Refs pra Ctrl+Enter conseguir chamar handleSave (declarado depois) sem
  // criar dependência circular no useCallback do handleInputKeyDown.
  const saveRef = React.useRef<{ canSave: boolean; save: () => void }>({
    canSave: false,
    save: () => undefined,
  });

  const handleInputKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Ctrl/Cmd + Enter: salva e fecha (mesmo que document listener, mas
      // garante quando foco está no input e o cmdk/Radix consome o evento)
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        if (saveRef.current.canSave) {
          e.preventDefault();
          e.stopPropagation();
          saveRef.current.save();
        }
        return;
      }

      // Ctrl/Cmd + Shift + A: toggle todos visíveis (sempre ganha)
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === 'a'
      ) {
        e.preventDefault();
        e.stopPropagation();
        toggleVisible();
        return;
      }

      // Ctrl/Cmd + Shift + X: limpa toda a seleção (inclusive não visíveis)
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === 'x'
      ) {
        e.preventDefault();
        e.stopPropagation();
        clearSelection();
        return;
      }

      // Autocomplete aberto: rouba ↑↓/⏎/Tab/Esc do cmdk
      if (ac.open) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          e.stopPropagation();
          setAc((s) => ({ ...s, idx: (s.idx + 1) % s.items.length }));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          e.stopPropagation();
          setAc((s) => ({
            ...s,
            idx: (s.idx - 1 + s.items.length) % s.items.length,
          }));
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          e.stopPropagation();
          applyAutocomplete();
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          setAc((s) => ({ ...s, open: false }));
          return;
        }
      }

      // Navegação na lista virtualizada (cmdk não controla mais)
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setActiveIndex((i) => Math.min(i + 1, combinedList.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setActiveIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        toggleAtIndex(activeIndex);
        return;
      }
    },
    [toggleVisible, clearSelection, ac, applyAutocomplete, combinedList.length, activeIndex, toggleAtIndex],
  );

  // Recomputa autocomplete sempre que o valor da busca muda.
  // Usa raf pra esperar o input refletir o novo value+caret.
  React.useEffect(() => {
    const id = requestAnimationFrame(updateAutocomplete);
    return () => cancelAnimationFrame(id);
  }, [searchValue, updateAutocomplete]);

  const selectOnly = React.useCallback(
    (name: string) => {
      setValue([name]);
    },
    [setValue],
  );

  const selectedSellers = React.useMemo(() => {
    return sellers.filter((seller) => value.includes(seller.id));
  }, [value, sellers]);

  const effectiveSelectedSellers = useAllSellers ? sellers : selectedSellers;

  const canISave = useAllSellers
    ? true
    : selectedSellers.length + sellersIDontHaveAccess.length >=
      MIN_SELECTED_SELLERS;

  const queryClient = useQueryClient();

  const handleSave = React.useCallback(() => {
    if (!useAllSellers) {
      // Só filiais ATIVAS: selectedSellers vem da lista do popup (lookup, que
      // traz apenas ativas) interseccionada com `value`. Inativas que entrem
      // pré-marcadas (default do getMe/select-all) não estão na lista e são
      // intencionalmente descartadas aqui. sellersIDontHaveAccess re-inclui
      // apenas as que o usuário não tem acesso.
      onSelect([...selectedSellers, ...sellersIDontHaveAccess]);
    }
    queryClient.invalidateQueries([FetchKeys.ME]);
    onOpenChange(false);
  }, [
    useAllSellers,
    onSelect,
    selectedSellers,
    sellersIDontHaveAccess,
    queryClient,
    onOpenChange,
  ]);

  // Mantém o ref do save em sync — handleInputKeyDown lê via saveRef.current
  saveRef.current = { canSave: canISave, save: handleSave };

  // Ctrl/Cmd + Enter salva e fecha (quando válido). Usa capture: true pra
  // garantir que ganhamos do focus trap do Radix Dialog. Também duplicamos
  // o handling no onKeyDown do input — listener no document fica como
  // safety net quando o foco está em outro lugar.
  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        if (!canISave) return;
        e.preventDefault();
        e.stopPropagation();
        handleSave();
        return;
      }
      // Ctrl/Cmd + / : liga/desliga "Ver todas as filiais". Fica no listener do
      // document (não no input) porque com o flag ligado o input fica disabled
      // e não receberia o keydown pra desligar de volta. Só ativo quando o toggle
      // existe (sidebar) — senão engoliria a tecla à toa nos demais seletores.
      if ((e.metaKey || e.ctrlKey) && e.key === '/' && onUseAllSellersChange) {
        e.preventDefault();
        e.stopPropagation();
        onUseAllSellersChange(!useAllSellers);
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, canISave, handleSave, useAllSellers, onUseAllSellersChange]);

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          // Fechou sem salvar (X, clique fora ou Esc): descarta tudo que foi
          // mexido e volta pro estado original (defaultValue).
          setValue(defaultValue.map((seller) => seller.id));
          setUnmatchedTokens([]);
          setSearchValue('');
          setActiveIndex(0);
          setAc((s) => ({ ...s, open: false }));
        }
        onOpenChange(open);
      }}
    >
      <Dialog.Content
        className="max-w-[60%]"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          searchInputRef.current?.focus();
        }}
      >
        <Dialog.Header>
          <div className="flex items-center justify-between gap-4">
            <Dialog.Title>{title || t('popup-title')}</Dialog.Title>
            <div className="mr-6 flex shrink-0 items-center gap-1.5 rounded-md border bg-muted/40 px-2.5 py-1 tabular-nums">
              <span className="text-foreground text-base font-bold leading-none">
                {effectiveSelectedSellers.length}
              </span>
              <span className="text-muted-foreground text-xs">
                {t('selected-of', { total: sellers.length })}
              </span>
            </div>
          </div>
          <Dialog.Description>
            {description || t('popup-description')}
          </Dialog.Description>
        </Dialog.Header>

        {showAllSellersToggle && (
          <div className="flex items-center justify-between gap-4 rounded-md border px-3 py-2">
            <div className="flex flex-col">
              <span className="text-xs font-medium">
                {t('all-sellers-toggle.label')}
              </span>
              <span className="text-muted-foreground text-[11px] leading-tight">
                <Trans
                  t={t}
                  i18nKey="all-sellers-toggle.description"
                  components={{
                    strong: <strong className="font-semibold" />,
                  }}
                />
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="flex items-center gap-0.5 opacity-60">
                <kbd className="bg-muted rounded border px-1 font-mono text-[9px] leading-none">
                  Ctrl
                </kbd>
                <kbd className="bg-muted rounded border px-1 font-mono text-[9px] leading-none">
                  /
                </kbd>
              </span>
              <Switch
                checked={useAllSellers}
                onCheckedChange={(checked) => {
                  const enabled = Boolean(checked);
                  if (!enabled) setValue([]); // ao sair do modo "todas", limpa a lista pro usuário escolher do zero
                  onUseAllSellersChange?.(enabled);
                }}
                aria-label={t('all-sellers-toggle.label')}
              />
            </div>
          </div>
        )}

        {!canISave && (
          <Alert variant="error" className="py-2">
            <Alert.Title className="text-sm">
              {t('invalid-seller-selection.title')}
            </Alert.Title>
            <Alert.Description className="text-xs">
              {t('invalid-seller-selection.description')}
            </Alert.Description>
          </Alert>
        )}

        {hasLoadError && (
          <Alert variant="warning" className="py-2">
            <Alert.Title className="text-sm">
              {t('partial-load-error.title')}
            </Alert.Title>
            <Alert.Description className="text-xs">
              {t('partial-load-error.description')}
            </Alert.Description>
          </Alert>
        )}

        {unmatchedTokens.length > 0 && (
          <Alert variant="warning" className="py-2">
            <Alert.Title className="text-sm">
              {t('bulk-paste.unmatched-title', {
                count: unmatchedTokens.length,
              })}
            </Alert.Title>
            <Alert.Description className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-mono text-xs">
                {unmatchedTokens.slice(0, 12).join(', ')}
                {unmatchedTokens.length > 12 ? '…' : ''}
              </span>
              <Button
                variant="ghost"
                className="h-6 px-2 text-xs"
                onClick={() => setUnmatchedTokens([])}
              >
                {t('bulk-paste.dismiss')}
              </Button>
            </Alert.Description>
          </Alert>
        )}

        <Command
          shouldFilter={false}
          className={cn(
            // overflow-visible derruba o overflow-hidden default do Command
            // (cmdk) — sem isso o popover de autocomplete fica clipado.
            'w-full overflow-visible rounded-md border',
            useAllSellers && 'pointer-events-none opacity-60',
          )}
        >
          <div className="relative">
            <Command.Input
              ref={searchInputRef}
              placeholder={t('search-for-sellers')}
              disabled={useAllSellers}
              value={searchValue}
              onValueChange={setSearchValue}
              onPaste={handlePaste}
              onKeyDown={handleInputKeyDown}
              onClick={updateAutocomplete}
              onKeyUp={(e) => {
                if (
                  [
                    'ArrowLeft',
                    'ArrowRight',
                    'Home',
                    'End',
                    'Backspace',
                    'Delete',
                  ].includes(e.key)
                ) {
                  updateAutocomplete();
                }
              }}
              onBlur={() => {
                // Pequeno delay pra permitir click no item do dropdown
                setTimeout(
                  () => setAc((s) => ({ ...s, open: false })),
                  120,
                );
              }}
            />
            {ac.open && (
              <div
                className="bg-popover text-popover-foreground absolute left-10 top-full z-50 mt-1 min-w-[280px] max-w-[400px] overflow-hidden rounded-md border shadow-lg"
                onMouseDown={(e) => e.preventDefault()}
              >
                <div className="bg-muted/40 text-muted-foreground flex items-center gap-2 border-b px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider">
                  <span>{ac.kind === 'facet' ? 'filtro' : ac.key}</span>
                  {ac.loading && (
                    <span className="text-muted-foreground/70 normal-case tracking-normal">
                      carregando…
                    </span>
                  )}
                  {!ac.loading && (
                    <span className="ml-auto flex items-center gap-1 text-[9px] normal-case tracking-normal">
                      <kbd className="bg-background rounded border px-1">
                        ↑↓
                      </kbd>
                      <kbd className="bg-background rounded border px-1">
                        ⏎
                      </kbd>
                      <span>aplicar</span>
                    </span>
                  )}
                </div>
                {ac.kind === 'facet' ? (
                  <div className="flex flex-wrap gap-1.5 p-2">
                    {ac.items.map((item, i) => {
                      const isActive = i === ac.idx;
                      return (
                        <button
                          key={item.value}
                          ref={isActive ? acActiveItemRef : undefined}
                          type="button"
                          onClick={() => {
                            setAc((s) => ({ ...s, idx: i }));
                            requestAnimationFrame(() => applyAutocomplete());
                          }}
                          onMouseEnter={() => setAc((s) => ({ ...s, idx: i }))}
                          className={cn(
                            'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                            isActive
                              ? 'bg-accent text-accent-foreground border-accent'
                              : 'hover:bg-accent/50',
                          )}
                        >
                          <span>{item.label}</span>
                          <span className="text-muted-foreground tabular-nums">
                            {item.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : ac.loading && ac.items.length === 0 ? (
                  <div className="text-muted-foreground flex items-center justify-center gap-2 py-6 text-xs">
                    <span className="border-muted-foreground/30 border-t-foreground h-3 w-3 animate-spin rounded-full border-2" />
                    <span>buscando opções</span>
                  </div>
                ) : ac.items.length === 0 ? (
                  <div className="text-muted-foreground py-4 text-center text-xs">
                    Nenhum resultado
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto p-1">
                    {ac.items.map((item, i) => {
                    const isActive = i === ac.idx;
                    return (
                      <button
                        key={item.value}
                        ref={isActive ? acActiveItemRef : undefined}
                        type="button"
                        onClick={() => {
                          setAc((s) => ({ ...s, idx: i }));
                          // chama applyAutocomplete depois do setAc commit
                          requestAnimationFrame(() => applyAutocomplete());
                        }}
                        onMouseEnter={() =>
                          setAc((s) => ({ ...s, idx: i }))
                        }
                        className={cn(
                          'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors',
                          isActive
                            ? 'bg-accent text-accent-foreground'
                            : 'hover:bg-accent/50',
                        )}
                      >
                        {ac.key === 'marcador' && item.color && (
                          <span
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: item.color }}
                          />
                        )}
                        {ac.key === 'estado' && item.value !== item.label && (
                          <span className="text-muted-foreground w-6 shrink-0 font-mono text-xs">
                            {item.value}
                          </span>
                        )}
                        {ac.key === 'pais' && item.flag && (
                          <span className="shrink-0 text-base leading-none">
                            {item.flag}
                          </span>
                        )}
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.sub && (
                          <span className="text-muted-foreground shrink-0 font-mono text-[10px]">
                            {item.sub}
                          </span>
                        )}
                        <span className="text-muted-foreground shrink-0 font-mono text-[10px] tabular-nums">
                          {item.count}
                        </span>
                      </button>
                    );
                  })}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Hint pra encorajar typing de syntax — clica injeta prefixo e
              abre o autocomplete inline com as opções. Mais leve/discoverable
              que dropdowns Estado/Cidade/Marcador separados. */}
          <div className="text-muted-foreground flex flex-wrap items-center gap-1.5 border-b px-3 py-1.5 font-mono text-[11px]">
            <span>filtre por</span>
            {(['estado:', 'cidade:', 'pais:', 'marcador:'] as const).map((prefix) => (
              <button
                key={prefix}
                type="button"
                // preventDefault no mousedown evita o blur do input — sem isso o
                // onBlur agenda um close de 120ms que fecha o autocomplete logo
                // depois do clique reabrir (race), fazendo parecer que "não abre".
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const needsSpace = searchValue && !searchValue.endsWith(' ');
                  const next =
                    (needsSpace ? searchValue + ' ' : searchValue) + prefix;
                  setSearchValue(next);
                  // Pre-foca + posiciona caret + força updateAutocomplete na
                  // próxima frame, garantindo que o input tem o novo value
                  // e o caret está no fim antes de calcular o token sob o
                  // cursor. Evita o caso onde a useEffect do searchValue roda
                  // antes do setSelectionRange e ac.open fica false.
                  requestAnimationFrame(() => {
                    const el = searchInputRef.current;
                    if (el) {
                      el.focus();
                      el.setSelectionRange(next.length, next.length);
                      updateAutocomplete();
                    }
                  });
                }}
                className="hover:bg-accent rounded border bg-white/50 px-1.5 py-0.5 transition-colors"
              >
                {prefix}
              </button>
            ))}
          </div>
          <div className="text-muted-foreground flex items-center justify-between border-b px-3 py-1.5 text-[11px]">
            <span>
              {isInitialLoading ? (
                <span className="text-muted-foreground/70">carregando…</span>
              ) : (
                <>
                  <span className="font-mono tabular-nums">
                    {visibleOptions.length}
                  </span>{' '}
                  {visibleOptions.length === 1 ? 'resultado' : 'resultados'}
                </>
              )}
              {sellersIDontHaveAccess.length > 0 && (
                <span className="ml-2">
                  ·{' '}
                  <span className="font-mono tabular-nums">
                    {sellersIDontHaveAccess.length}
                  </span>{' '}
                  sem acesso
                </span>
              )}
              {!isInitialLoading && isLoadingMoreSellers && (
                <span className="ml-2 text-muted-foreground/70">
                  · carregando mais filiais… (
                  <span className="font-mono tabular-nums">
                    {enrichedSellersData.length}
                  </span>
                  {' de '}
                  <span className="font-mono tabular-nums">
                    {lookupTotal}
                  </span>
                  )
                </span>
              )}
            </span>
            <span className="flex items-center gap-1">
              <kbd className="bg-muted text-muted-foreground rounded border px-1 font-mono text-[10px]">
                Ctrl
              </kbd>
              <kbd className="bg-muted text-muted-foreground rounded border px-1 font-mono text-[10px]">
                ⇧
              </kbd>
              <kbd className="bg-muted text-muted-foreground rounded border px-1 font-mono text-[10px]">
                A
              </kbd>
              <span className="ml-1">alternar visíveis</span>
            </span>
          </div>
          {isInitialLoading ? (
            <div className="h-[36rem] overflow-hidden">
              {Array.from({ length: 9 }).map((_, i) => (
                <div
                  key={i}
                  className="flex h-[72px] items-center gap-3 px-3"
                >
                  <Skeleton className="h-4 w-4 shrink-0 rounded-md" />
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-4 w-28 rounded-full" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                    <Skeleton className="h-3 w-40" />
                    <Skeleton className="h-2.5 w-56" />
                  </div>
                  <Skeleton className="h-5 w-16 shrink-0 rounded-full" />
                </div>
              ))}
            </div>
          ) : combinedList.length === 0 ? (
            <div className="text-muted-foreground py-6 text-center text-sm">
              {t('no-seller-found')}
            </div>
          ) : (
            <div className="h-[36rem]">
              <AutoSizer>
                {({ width, height }) => (
                  <VirtualList
                    ref={listRef}
                    width={width}
                    height={height}
                    rowCount={combinedList.length}
                    rowHeight={72}
                    scrollToIndex={activeIndex}
                    scrollToAlignment="auto"
                    overscanRowCount={6}
                    rowRenderer={({ index, key, style }) => {
                      const option = combinedList[index];
                      const isSelected = value.includes(option.name);
                      const isActive = index === activeIndex;
                      return (
                        <div key={key} style={style}>
                          <VirtualRow
                            option={option}
                            isSelected={isSelected}
                            isActive={isActive}
                            isOnlyOne={
                              value.length === 1 && value[0] === option.name
                            }
                            onToggle={() => toggleAtIndex(index)}
                            onSelectOnly={() => selectOnly(option.name)}
                            onSelectAll={selectAll}
                            onHover={() => setActiveIndex(index)}
                          />
                        </div>
                      );
                    }}
                  />
                )}
              </AutoSizer>
            </div>
          )}
        </Command>

        <Dialog.Footer>
          <div className="flex flex-1 items-center gap-2">
            <Button
              variant="outline"
              onClick={toggleVisible}
              disabled={useAllSellers || visibleOptions.length === 0}
              className="gap-2"
            >
              {allVisibleSelected
                ? t('multi-select-button.unselect-visible')
                : t('multi-select-button.select-visible')}
              <span className="flex items-center gap-0.5 opacity-60">
                <kbd className="bg-muted rounded border px-1 font-mono text-[9px] leading-none">
                  Ctrl
                </kbd>
                <kbd className="bg-muted rounded border px-1 font-mono text-[9px] leading-none">
                  ⇧
                </kbd>
                <kbd className="bg-muted rounded border px-1 font-mono text-[9px] leading-none">
                  A
                </kbd>
              </span>
            </Button>
            <Button
              variant="ghost"
              onClick={clearSelection}
              disabled={useAllSellers || value.length === 0}
              className="gap-2"
            >
              {t('clear-selection', { count: value.length })}
              <span className="flex items-center gap-0.5 opacity-60">
                <kbd className="bg-muted rounded border px-1 font-mono text-[9px] leading-none">
                  Ctrl
                </kbd>
                <kbd className="bg-muted rounded border px-1 font-mono text-[9px] leading-none">
                  ⇧
                </kbd>
                <kbd className="bg-muted rounded border px-1 font-mono text-[9px] leading-none">
                  X
                </kbd>
              </span>
            </Button>
          </div>
          <Dialog.Close asChild>
            <Button variant="outline" className="gap-2">
              Cancelar
              <span className="flex items-center gap-0.5 opacity-60">
                <kbd className="bg-muted rounded border px-1 font-mono text-[9px] leading-none">
                  Esc
                </kbd>
              </span>
            </Button>
          </Dialog.Close>

          <Button
            onClick={handleSave}
            disabled={!canISave}
            className="gap-2"
          >
            Salvar
            <span className="flex items-center gap-0.5 opacity-60">
              <kbd className="bg-background/20 rounded border border-white/20 px-1 font-mono text-[9px] leading-none">
                Ctrl
              </kbd>
              <kbd className="bg-background/20 rounded border border-white/20 px-1 font-mono text-[9px] leading-none">
                ⏎
              </kbd>
            </span>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
};

interface VirtualRowProps {
  option: {
    label: React.ReactNode;
    name: string;
  };
  isSelected: boolean;
  isActive: boolean;
  isOnlyOne: boolean;
  onToggle: () => void;
  onSelectAll: () => void;
  onSelectOnly: () => void;
  onHover: () => void;
}

// Row plano da lista virtualizada. Sem Command.Item porque react-virtualized
// só mantém no DOM as ~10 linhas visíveis — cmdk não conseguiria rastrear
// activeIndex coerente nesse cenário. Quem cuida do highlight é o pai via
// activeIndex/isActive; click ou ⏎ chama onToggle.
const VirtualRow = ({
  option,
  isSelected,
  isActive,
  isOnlyOne,
  onToggle,
  onSelectAll,
  onSelectOnly,
  onHover,
}: VirtualRowProps) => {
  return (
    <div
      role="option"
      aria-selected={isSelected}
      onClick={onToggle}
      onMouseEnter={onHover}
      className={cn(
        'group flex h-full cursor-pointer select-none items-center gap-2 px-3 text-sm transition-colors',
        isActive && 'bg-accent text-accent-foreground',
        isSelected && !isActive && 'bg-accent/40',
      )}
    >
      <div
        className={cn(
          'border-primary flex h-4 w-4 shrink-0 items-center justify-center rounded-md border',
          isSelected
            ? 'bg-primary text-primary-foreground'
            : 'opacity-50 [&_svg]:invisible',
        )}
      >
        <Check className="h-4 w-4" />
      </div>
      {option.label}
      {isOnlyOne ? (
        <Button
          className="ml-auto shrink-0 p-0 pl-1 opacity-0 transition-opacity group-hover:opacity-100"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onSelectAll();
          }}
        >
          Todos
        </Button>
      ) : (
        <Button
          className="ml-auto shrink-0 p-0 pl-1 opacity-0 transition-opacity group-hover:opacity-100"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onSelectOnly();
          }}
        >
          Apenas
        </Button>
      )}
    </div>
  );
};

export default SelectSellersDialog;
